import React, { ReactNode, useEffect } from 'react';

import ReactTooltip from 'react-tooltip';

import Typography from './typography';
import QuestionMarkIcon from './questionmarkicon';

import '@styles/tooltip.scss';

interface TooltipProps {
  id: string;
  text: string;
  children: ReactNode;
  delayHide?: number;
  'data-cy'?: string;
  'data-testid'?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  id,
  text,
  children,
  delayHide = 1000,
  'data-cy': dataCy,
  'data-testid': dataTestid,
}) => {
  const [isTooltipOpen, setTooltipOpen] = React.useState(false);
  const tooltipWrapperRef = React.useRef(null);

  const hideTooltipHandler = React.useCallback(() => {
    const tooltipWrapperEl = tooltipWrapperRef.current;
    if (tooltipWrapperEl) {
      ReactTooltip.hide(tooltipWrapperEl);
      setTooltipOpen(false);
      document.removeEventListener('click', hideTooltipHandler);
    }
  }, []);

  const onTooltipTriggerClick = React.useCallback(
    (hideTooltipHandlerFn) => {
      const tooltipWrapperEl = tooltipWrapperRef.current;
      if (tooltipWrapperEl) {
        if (isTooltipOpen) {
          ReactTooltip.hide(tooltipWrapperEl);
          setTooltipOpen(false);
        } else {
          ReactTooltip.show(tooltipWrapperEl);
          setTooltipOpen(true);

          document.addEventListener('click', hideTooltipHandlerFn);
        }
      }
    },
    [isTooltipOpen]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('click', hideTooltipHandler);
    };
  }, [hideTooltipHandler]);

  return (
    <div className="tooltip" data-cy={dataCy} data-testid={dataTestid}>
      <Typography variant="caption" size="rg">
        {text}
      </Typography>
      <div
        data-tip
        data-for={id}
        ref={tooltipWrapperRef}
        onClick={onTooltipTriggerClick}
      >
        <QuestionMarkIcon />
      </div>
      <ReactTooltip
        id={id}
        place="top"
        border
        isCapture
        effect="solid"
        delayHide={delayHide}
        className="tooltip__content"
        role="status"
      >
        {children}
      </ReactTooltip>
    </div>
  );
};

export default Tooltip;
